/*
 * Sign-in and step-up.
 *   Mini App   POST /auth/telegram-webapp { initData } — silent.
 *   Browser    Telegram Login Widget → POST /auth/telegram, or an email magic link.
 *   Step-up    a 401 step_up_required opens a sheet asking for a fresh Telegram login
 *              (Mini App: re-send initData when auth_date < 10 min, otherwise reopen),
 *              then the original request is retried by the API client.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ApiError, onStepUpRequired } from "../api/client";
import { endpoints } from "../api/endpoints";
import type { ConfigResponse, TelegramLoginPayload } from "../api/types";
import { BeaconMark, Chip, FieldRow, I, Notice, Section, Spinner } from "../components/tk";
import { Sheet } from "../components/tk/overlays";
import { closeMiniApp, haptic, initAuthDate, initData, isMiniApp } from "../lib/tg";
import { useConfig, useMeQuery } from "./session";

declare global {
  interface Window {
    __beaconTgAuth?: (user: TelegramLoginPayload) => void;
  }
}

/** The official Telegram Login Widget, rendered into a container. */
export function TelegramLoginButton({ botUsername, onAuth, size = "large" }: { botUsername: string; onAuth: (u: TelegramLoginPayload) => void; size?: "large" | "medium" }) {
  const ref = useRef<HTMLDivElement>(null);
  const cb = useRef(onAuth);
  cb.current = onAuth;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    window.__beaconTgAuth = (u) => cb.current(u);
    const s = document.createElement("script");
    s.src = "https://telegram.org/js/telegram-widget.js?22";
    s.async = true;
    s.setAttribute("data-telegram-login", botUsername);
    s.setAttribute("data-size", size);
    s.setAttribute("data-radius", "12");
    s.setAttribute("data-request-access", "write");
    s.setAttribute("data-onauth", "__beaconTgAuth(user)");
    el.appendChild(s);
    return () => {
      el.innerHTML = "";
    };
  }, [botUsername, size]);
  return <div ref={ref} className="min-h-[40px] flex justify-center" />;
}

/** Dev-only stand-in for the widget (the widget needs a registered domain). */
function devPayload(): TelegramLoginPayload {
  return { id: 100200300, first_name: "Rishabh", username: "rishabh", auth_date: Math.floor(Date.now() / 1000), hash: "dev" };
}

export function AuthGate({ children }: { children: (config: ConfigResponse) => ReactNode }) {
  const config = useConfig();
  const me = useMeQuery();
  const qc = useQueryClient();
  const [miniAppError, setMiniAppError] = useState<string | null>(null);
  const triedWebApp = useRef(false);
  const unauth = me.error instanceof ApiError && me.error.status === 401;

  useEffect(() => {
    if (!unauth || !isMiniApp || triedWebApp.current) return;
    triedWebApp.current = true;
    endpoints
      .authWebApp({ initData })
      .then(() => qc.invalidateQueries({ queryKey: ["me"] }))
      .catch((e) => setMiniAppError(e instanceof Error ? e.message : "Sign-in failed"));
  }, [unauth, qc]);

  if (config.isLoading || me.isLoading || (unauth && isMiniApp && !miniAppError)) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Spinner label="Opening Beacon…" />
      </div>
    );
  }
  if (config.error || !config.data) {
    return (
      <div className="grid min-h-dvh place-items-center p-6 text-center">
        <div>
          <p className="text-ink-2">Beacon can’t reach its server.</p>
          <Chip auto onClick={() => location.reload()}>Retry</Chip>
        </div>
      </div>
    );
  }
  if (me.data) return <>{children(config.data)}</>;
  return <Login config={config.data} miniAppError={miniAppError} />;
}

function Login({ config, miniAppError }: { config: ConfigResponse; miniAppError: string | null }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Magic-link callback: /auth/email/callback?token=…
  useEffect(() => {
    const token = new URLSearchParams(location.search).get("token");
    if (!location.pathname.endsWith("/auth/email/callback") || !token) return;
    setBusy("email");
    endpoints
      .authEmailVerify({ token })
      .then(() => {
        history.replaceState(null, "", "/");
        return qc.invalidateQueries({ queryKey: ["me"] });
      })
      .catch((e) => setErr(e.message))
      .finally(() => setBusy(null));
  }, [qc]);

  const done = () => {
    haptic.notify("success");
    return qc.invalidateQueries({ queryKey: ["me"] });
  };
  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Sign-in failed");
      haptic.notify("error");
    } finally {
      setBusy(null);
    }
  };

  return (
    <main className="min-h-dvh px-4 py-10 md:grid md:place-items-center">
      <div className="mx-auto w-full max-w-[420px]">
        <div className="flex flex-col items-center text-center">
          <BeaconMark size={72} />
          <h1 className="mt-5 mb-0 text-[28px] font-bold tracking-tight">Beacon</h1>
          <p className="mt-2 mb-0 max-w-[320px] text-[15px] leading-snug text-ink-2">Set a goal and a budget. We hit your cost per real customer.</p>
        </div>
        {miniAppError && (
          <div className="mt-6">
            <Notice tone="warn">Telegram sign-in failed: {miniAppError}. Close and reopen Beacon from the bot.</Notice>
          </div>
        )}
        <Section header="Sign in" footer="Beacon never posts or messages on your behalf. Your Telegram Ads token is connected separately.">
          <div className="flex flex-col items-center gap-3 px-4 py-5">
            <TelegramLoginButton botUsername={config.bot_username} onAuth={(u) => run("tg", () => endpoints.authTelegram(u).then(done))} />
            {config.features.dev_login && (
              <Chip auto icon="bolt" loading={busy === "dev"} onClick={() => run("dev", () => endpoints.devLogin({ name: "Rishabh" }).then(done))}>
                Continue as dev user
              </Chip>
            )}
          </div>
        </Section>
        {config.features.email_login && (
          <Section header="Or use email" footer={sent ? "Check your inbox. The link works for 15 minutes." : "We’ll send you a one-time sign-in link."}>
            {sent ? (
              <div className="tk-row">
                <I.check width={20} height={20} className="text-green" />
                <span className="tk-label">Link sent to {email}</span>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  run("email", () => endpoints.authEmail({ email }).then(() => setSent(true)));
                }}
              >
                <FieldRow label="Email" type="email" value={email} onChange={setEmail} placeholder="you@company.com" name="email" />
                <div className="px-4 pb-3">
                  <Chip type="submit" tone="primary" loading={busy === "email"} disabled={!/.+@.+\..+/.test(email)} className="w-full">
                    Send sign-in link
                  </Chip>
                </div>
              </form>
            )}
          </Section>
        )}
        {err && (
          <div className="mt-4">
            <Notice tone="warn">{err}</Notice>
          </div>
        )}
      </div>
    </main>
  );
}

/* ---------------- step-up ---------------- */

type Pending = { reason: string; resolve: () => void; reject: (e: Error) => void };

/** Registers the step-up handler with the API client and draws the confirmation sheet. */
export function StepUpHost() {
  const config = useConfig().data;
  const qc = useQueryClient();
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    onStepUpRequired(
      (reason) =>
        new Promise<void>((resolve, reject) => {
          const fresh = isMiniApp && initAuthDate != null && Date.now() / 1000 - initAuthDate < 600;
          if (fresh) {
            // Mini App: the launch init data is still fresh enough — re-send it silently.
            endpoints
              .stepUp({ initData })
              .then(() => resolve())
              .catch(() => setPending({ reason, resolve, reject }));
            return;
          }
          setErr(null);
          setPending({ reason, resolve, reject });
        }),
    );
    return () => onStepUpRequired(null);
  }, []);

  const confirm = async (payload: TelegramLoginPayload) => {
    if (!pending) return;
    setBusy(true);
    setErr(null);
    try {
      await endpoints.stepUp({ telegram: payload });
      haptic.notify("success");
      qc.invalidateQueries({ queryKey: ["me"] });
      pending.resolve();
      setPending(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn’t confirm");
      haptic.notify("error");
    } finally {
      setBusy(false);
    }
  };
  const cancel = () => {
    pending?.reject(new ApiError(401, { code: "step_up_required", message: "Confirmation cancelled" }));
    setPending(null);
  };

  return (
    <Sheet open={!!pending} onClose={cancel} title="Confirm it’s you" lede={pending?.reason || "This action changes money or access. Confirm with a fresh Telegram login."}>
      <div className="flex flex-col items-center gap-4 pb-4 pt-2">
        <div className="grid size-16 place-items-center rounded-full bg-accent-tint text-accent-ink">
          <I.shield width={30} height={30} />
        </div>
        {isMiniApp ? (
          <>
            <p className="m-0 max-w-[340px] text-center text-[14px] leading-snug text-ink-2">
              Your Telegram session is older than 10 minutes. Close Beacon and open it again from the bot, then repeat the action.
            </p>
            <Chip tone="primary" auto onClick={closeMiniApp}>
              Close and reopen
            </Chip>
          </>
        ) : (
          config && (
            <>
              <TelegramLoginButton botUsername={config.bot_username} onAuth={confirm} size="large" />
              {config.features.dev_login && (
                <Chip auto icon="bolt" loading={busy} onClick={() => confirm(devPayload())}>
                  Confirm as dev user
                </Chip>
              )}
            </>
          )
        )}
        {err && <Notice tone="warn">{err}</Notice>}
        <p className="m-0 text-[12.5px] text-ink-3">Valid for 10 minutes after you confirm.</p>
      </div>
    </Sheet>
  );
}
